<template>
  <article class="article-card">
    <NuxtLink :to="postUrl" prefetch-on="interaction" class="article-link">
      <div class="article-media">
        <img
          v-if="image"
          :src="responsiveImage?.src || image"
          :srcset="responsiveImage?.srcset || imageSet"
          :sizes="responsiveImage?.sizes"
          :data-original-src="responsiveImage?.original"
          :fetchpriority="priority ? 'high' : 'auto'"
          onerror="
            this.onerror = null;
            this.removeAttribute('srcset');
            if (this.dataset.originalSrc) {
              this.src = this.dataset.originalSrc;
              delete this.dataset.originalSrc;
            }
          "
          alt=""
          width="588"
          height="330"
          :loading="eager ? 'eager' : 'lazy'"
          decoding="async"
          class="article-image"
        />
        <span v-else class="article-placeholder" aria-hidden="true"
          >Idmanify</span
        >
      </div>
      <div class="article-body">
        <div v-if="post.category?.name || date" class="article-meta">
          <span v-if="post.category?.name">{{ post.category.name }}</span>
          <time v-if="date" :datetime="date.iso">{{ date.label }}</time>
        </div>
        <component :is="heading" class="article-title">{{
          post.title
        }}</component>
        <p v-if="excerpt" class="article-excerpt">{{ excerpt }}</p>
        <span class="article-action"
          >{{ t('conversion.readArticle') }}<EditorialArrow
        /></span>
      </div>
    </NuxtLink>
  </article>
</template>
<script setup>
import { articleCardImageSources } from '~/utils/article-image';
import { articleDate, plainExcerpt } from '~/utils/editorial';
const props = defineProps({
  post: { type: Object, required: true },
  heading: {
    type: String,
    default: 'h3',
    validator: (value) => ['h2', 'h3'].includes(value)
  },
  eager: { type: Boolean, default: false },
  priority: { type: Boolean, default: false }
});
const { t, locale } = useI18n();
const config = useRuntimeConfig();
const responsiveImage = computed(() =>
  articleCardImageSources(
    props.post.image || props.post.thumbnail || '',
    config.public.API_URL,
    config.app.baseURL
  )
);
const localePath = useLocalePath();
const postUrl = computed(() => localePath(`/post/${props.post.slug}`));
const image = computed(
  () => props.post.thumbnail || props.post.image || props.post.picture || ''
);
// Prefer the small API thumbnail on standard-density screens; retain a crisp
// source for high-density displays without an image proxy or client-side loader.
const imageSet = computed(() =>
  props.post.thumbnail &&
  props.post.image &&
  props.post.thumbnail !== props.post.image
    ? `${props.post.thumbnail} 1x, ${props.post.image} 2x`
    : undefined
);
const date = computed(() => articleDate(props.post, locale.value));
const excerpt = computed(() => plainExcerpt(props.post.shortDescription));
</script>
<style scoped>
.article-card {
  min-width: 0;
  height: 100%;
  overflow: hidden;
  border-radius: 1rem;
  background: #fff;
  border: 1px solid #d7d9dc;
  color: #17191d;
  transition:
    border-color 180ms ease,
    box-shadow 180ms ease,
    transform 180ms ease;
}
.article-link {
  display: flex;
  height: 100%;
  flex-direction: column;
  color: inherit;
  text-decoration: none;
  font-weight: 400;
}
.article-media {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: #f6f5f2;
}
.article-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 240ms ease;
}
.article-placeholder {
  display: grid;
  position: absolute;
  inset: 0;
  place-items: center;
  color: #676b71;
  font-weight: 750;
}
.article-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: clamp(1.1rem, 2vw, 1.5rem);
}
.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-block-end: 0.9rem;
  color: #505761;
  font-size: 0.8rem;
  line-height: 1.5;
}
.article-meta > span {
  color: #9a3412;
  font-weight: 750;
}
.article-title {
  font-size: 1.2rem;
  font-weight: 750;
  line-height: 1.4;
  text-wrap: pretty;
}
.article-excerpt {
  margin-block: 1rem;
  color: #444b55;
  font-size: 0.95rem;
  line-height: 1.65;
}
.article-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-block-start: auto;
  padding-block-start: 1.25rem;
  color: #9a3412;
  font-weight: 750;
}
.article-body :is(h2, h3, p, span, time) {
  overflow-wrap: anywhere;
}
.article-link:focus-visible {
  outline: 3px solid #c2410c;
  outline-offset: -4px;
  border-radius: 1rem;
}
.article-link:focus-visible .article-title {
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}
@media (hover: hover) and (pointer: fine) {
  .article-card:hover {
    border-color: #b59b8d;
    box-shadow: 0 12px 30px -16px rgb(23 25 29 / 25%);
    transform: translateY(-3px);
  }
  .article-link:hover .article-image {
    transform: scale(1.025);
  }
  .article-link:hover .article-title {
    text-decoration: underline;
    text-underline-offset: 0.2rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .article-card,
  .article-image {
    transition: none;
    transform: none !important;
  }
}
</style>
